import { useEffect, useState } from "react";
import { ReactFlow, Controls, Background } from "reactflow";
import "reactflow/dist/style.css";
import { backendGet } from "services/dataService.js";
import { Typography, Button } from "@mui/material";

const ClaimFlowChart = ({
  onStatusClick,
  orientation = "horizontal",
  statusCounts = {},
  statusDescriptions = {},
}) => {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [highlightedEdges, setHighlightedEdges] = useState([]);

  const [states, setStates] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const selectedState = selectedNode
    ? states?.find((state) => state.name === selectedNode)
    : null;

  useEffect(() => {
    fetchStatesAndTransitions();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setSelectedNode(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const fetchStatesAndTransitions = async () => {
    try {
      const response = await backendGet("data/schadens/states", {
        label: "Schaden States",
      });
      const { states, transitions } = response.data;
      setStates(states);

      const nodePositions = calculateNodePositions(
        states,
        transitions,
        orientation
      );

      const nodes = states.map((state, index) => ({
        id: state.name,
        data: {
          label: (
            <div style={{ position: "relative" }}>
              {state.label}
              {statusCounts[state.name] && (
                <span
                  style={{
                    position: "absolute",
                    top: -20,
                    right: -20,
                    backgroundColor: "black",
                    color: "white",
                    borderRadius: "50%",
                    width: "20px",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                >
                  {statusCounts[state.name]}
                </span>
              )}
            </div>
          ),
        },
        position: nodePositions[state.name],
        style: {
          zIndex: 10,
          border: "1px solid gray",
          backgroundColor: "white",
          borderRadius:
            index === 0 || index === states.length - 1 ? "15px" : "0",
        },
      }));

      const edges = Object.entries(transitions).flatMap(([from, toStates]) =>
        toStates.map((to) => ({
          id: `${from}-${to}`,
          source: from,
          target: to,
          animated: true,
          style: { zIndex: 1 },
        }))
      );

      setNodes(nodes);
      setEdges(edges);

      const shortestPath = findShortestPath(transitions, "saved", "closed");
      setHighlightedEdges(shortestPath);
    } catch (error) {
      console.error("Fehler beim Abrufen der States und Transitions:", error);
    }
  };

  const calculateNodePositions = (states, transitions, orientation) => {
    const positions = {};
    const spacing = 100; // Reduzierter Abstand

    states.forEach((state, index) => {
      const incomingEdges = Object.values(transitions).filter((targets) =>
        targets.includes(state.name)
      ).length;
      const outgoingEdges = transitions[state.name]?.length || 0;

      positions[state.name] =
        orientation === "horizontal"
          ? { x: index * spacing, y: (incomingEdges - outgoingEdges) * spacing }
          : {
              x: (incomingEdges - outgoingEdges) * spacing,
              y: index * spacing,
            };
    });

    return positions;
  };

  const findShortestPath = (transitions, start, end) => {
    const visited = new Set();
    const queue = [[start]];
    const paths = [];

    while (queue.length > 0) {
      const path = queue.shift();
      const node = path[path.length - 1];

      if (node === end) {
        paths.push(path);
        break;
      }

      if (!visited.has(node)) {
        visited.add(node);
        const neighbors = transitions[node] || [];
        neighbors.forEach((neighbor) => {
          queue.push([...path, neighbor]);
        });
      }
    }

    return (
      paths[0]?.slice(1).map((target, index) => ({
        id: `${paths[0][index]}-${target}`,
        source: paths[0][index],
        target,
      })) || []
    );
  };

  const handleNodeClick = (event, node) => {
    setSelectedNode(node.id);
    if (onStatusClick) {
      onStatusClick(node.id);
    }
  };

  return (
    <div style={{ height: "500px", position: "relative" }}>
      <ReactFlow
        nodes={nodes}
        edges={edges.map((edge) => ({
          ...edge,
          style: highlightedEdges.some((e) => e.id === edge.id)
            ? { stroke: "red", strokeWidth: 2 }
            : edge.style,
        }))}
        fitView
        onNodeClick={handleNodeClick}
      >
        <Controls />
        <Background color="#aaa" gap={16} />
      </ReactFlow>

      {selectedNode && statusDescriptions && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "white",
            border: "1px solid black",
            borderRadius: "10px",
            padding: "20px",
            zIndex: 1000,
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
          }}
        >
          <Typography variant="body1" fontWeight="bold">
            {selectedState?.label}
          </Typography>
          <Typography
            variant="caption"
            style={{ marginBottom: "10px", display: "block" }}
          >
            {statusDescriptions[selectedNode] ||
              "Keine Beschreibung verfügbar."}
          </Typography>
          <Button onClick={() => setSelectedNode(null)} size="small">
            Schließen
          </Button>
        </div>
      )}
    </div>
  );
};

export default ClaimFlowChart;
